// material-ui
import {
    Box,
    Collapse,
    FormControl,
    IconButton,
    InputAdornment,
    OutlinedInput,
} from '@mui/material';

// assets
import { GlobalOutlined, SearchOutlined } from '@ant-design/icons';
import {
    EventHandler,
    KeyboardEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from '../../../../contexts/TranslationContext';
import useApi from '../../../../api';
import { SearchResult as ResultType } from '../../../../api/types/search';
import SearchResult, {
    ResultRef,
} from '../../../../components/search/SearchResult';
import { useSearchContext } from '../../../../contexts/SearchContext';
import { useTheme } from '@mui/material/styles';

// ==============================|| HEADER CONTENT - SEARCH ||============================== //

const Search = () => {
    const placeHolderRef = useRef<HTMLElement>();

    const resultRef = useRef<ResultRef>();
    const searchRef = useRef<HTMLInputElement>();

    const { search } = useApi();
    const { translate } = useTranslation();
    const theme = useTheme();

    const { strictType, type, toggleStrict } = useSearchContext();

    const [inputFull, setInputFull] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<ResultType>();

    const cmdOrCtrl = () =>
        window.navigator.platform.match(/^Mac/)
            ? 'static.search.cmd_key'
            : 'static.search.ctrl_key';

    const runSearch = () => {
        if (inputFull && searchQuery && searchQuery.length > 0) {
            search
                .executeSearch(searchQuery, strictType ? type : 'all')
                .then(response => {
                    if (response && !response.error) {
                        setSearchResults(response);
                    }
                });
        }
    };

    useEffect(runSearch, [searchQuery, strictType, type]);

    useEffect(() => {
        setInputFull(false);
    }, []);

    const toggleGlobalSearch = useCallback(
        (event: KeyboardEvent) => {
            // Ctrl / Cmd  -  G   (Toggle global search)
            if ((event.ctrlKey || event.metaKey) && event.keyCode === 71) {
                event.preventDefault();
                toggleStrict();
            }
        },
        [strictType],
    );

    const onKeyPressedInSearchBar = (event: KeyboardEvent) => {
        toggleGlobalSearch(event);
        if (inputFull) {
            if (
                event.key === 'ArrowDown' ||
                event.key === 'ArrowUp' ||
                event.key === 'Enter'
            ) {
                event.preventDefault();
                if (event.key === 'ArrowUp') {
                    resultRef.current?.selector.up();
                }
                if (event.key === 'ArrowDown') {
                    resultRef.current?.selector.down();
                }
                if (event.key === 'Enter') {
                    resultRef.current?.selector.select();
                }
            }
        }
    };

    useEffect(() => {
        searchRef.current?.addEventListener(
            'keydown',
            onKeyPressedInSearchBar as EventHandler<any>,
        );
        return () => {
            if (searchRef.current) {
                searchRef.current.removeEventListener(
                    'keydown',
                    onKeyPressedInSearchBar as EventHandler<any>,
                );
            }
        };
    }, [inputFull, toggleGlobalSearch]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            toggleGlobalSearch(event);

            // Ctrl / Cmd  - F   (Focus in search)
            if ((event.ctrlKey || event.metaKey) && event.keyCode === 70) {
                event.preventDefault();
                searchRef.current?.focus();
            }
            // Esc   (Blur search / focus out search)
            if (event.key === 'Escape' || event.keyCode === 27) {
                event.preventDefault();
                searchRef.current?.blur();
            }
        };

        const onFocus = () => {
            setInputFull(true);
        };

        const onBlur = () => {
            resultRef.current?.close();
            setTimeout(() => setInputFull(false), 100);
        };

        document.addEventListener('keydown', onKeyDown as EventHandler<any>);
        searchRef.current?.addEventListener('focus', onFocus);
        searchRef.current?.addEventListener('blur', onBlur);
        return () => {
            document.removeEventListener(
                'keydown',
                onKeyDown as EventHandler<any>,
            );
            if (searchRef.current) {
                searchRef.current.removeEventListener('focus', onFocus);
                searchRef.current?.removeEventListener('blur', onBlur);
            }
        };
    }, [searchRef.current]);

    const renderEndAdornment = useCallback(() => {
        const title = strictType
            ? translate('form.general.global_inactive')
            : translate('form.general.global_active');

        return (
            <IconButton onClick={toggleStrict} title={title}>
                <GlobalOutlined
                    style={{
                        color: strictType
                            ? theme.palette.error.main
                            : theme.palette.success.main,
                    }}
                />
            </IconButton>
        );
    }, [strictType]);

    return (
        <Box sx={{ width: '100%', ml: { xs: 0, md: 1 } }}>
            <FormControl sx={{ display: 'flex', flexGrow: 1, width: '100%' }}>
                <Collapse
                    collapsedSize={200}
                    orientation="horizontal"
                    in={inputFull}
                    appear={true}
                    addEndListener={event => {
                        if (event.clientWidth <= 200) {
                            setTimeout(() => {
                                if (
                                    (searchRef.current?.value ?? '').length > 0
                                ) {
                                    runSearch();
                                    resultRef.current?.open();
                                }
                            }, 100);
                        }
                    }}
                    mountOnEnter={true}
                    unmountOnExit={false}
                    sx={{
                        width: '100%',
                        '.MuiCollapse-wrapper': {
                            width: '100%',
                        },
                        '.MuiCollapse-wrapperInner': {
                            width: 'inherit',
                        },
                    }}>
                    <OutlinedInput
                        inputRef={searchRef}
                        size="small"
                        id="header-search"
                        value={searchQuery}
                        autoComplete="off"
                        aria-autocomplete="none"
                        autoCapitalize="off"
                        autoCorrect="off"
                        startAdornment={
                            <InputAdornment position="start" sx={{ mr: -0.5 }}>
                                <SearchOutlined />
                            </InputAdornment>
                        }
                        endAdornment={renderEndAdornment()}
                        sx={{ pl: 1.5, pr: 0 }}
                        fullWidth
                        onChange={event => setSearchQuery(event.target.value)}
                        aria-describedby="header-search-text"
                        inputProps={{
                            'aria-label': 'weight',
                        }}
                        placeholder={translate(cmdOrCtrl())}
                    />
                </Collapse>
                <div
                    ref={ref => {
                        if (ref) {
                            placeHolderRef.current = ref;
                        }
                    }}
                />
                <SearchResult
                    anchorEl={placeHolderRef}
                    resultRef={resultRef}
                    searchResults={searchResults}
                    onItemSelected={() => searchRef.current?.blur()}
                />
            </FormControl>
        </Box>
    );
};

export default Search;
